import { MainTabsV2, StatusChipBg } from '@labrav/react-components'

import { useState } from 'react'
import { useStyles } from './BuyerDetailsTab.style'
import RevenueCard from './Revenues'
import { useSelector } from '../../../store/hooks'

interface Status {
  statusText?: string
  statusColor: StatusChipBg
}
import { Contract, Contracts } from '../../../modules/buyerContracts/types'
import { Revenues } from '../../../modules/buyerRevenues/types'
import { DateTime } from 'luxon'
import { standardLabraDateFormat } from '@labrav/common'
import ContactCard from './Contacts'
import { Contacts } from '../../../modules/buyerContacts/types'
import { Meterings } from '../../../modules/buyerMeterings/types'
import MeteringCard from './Meterings'
import { OverviewEmptyState } from '../../FlyoutOverview/OverviewEmptyState/OverviewEmptyState'
import OverviewEmptyStateImage from '../../../common/images/overviewEmptyState.png'
import { ContractsList } from './ContractsList'

export default () => {
  const { classes } = useStyles()
  const timeZone = useSelector<string>(
    state => state.userProfile.userProfile?.timeZone || 'UTC'
  )
  const { contracts } = useSelector<Contracts>(state => state.BuyerContracts)
  const { revenues } = useSelector<Revenues>(state => state.BuyerRevenues)
  const { contacts } = useSelector<Contacts>(state => state.BuyerContacts)
  const { meterings } = useSelector<Meterings>(state => state.BuyerMeterings)

  const [visibleContractData, setVisibleContractData] = useState<Contract[]>([])
  const [contractPage, setContractPage] = useState(1)

  const mapMeteringStatus = (status: string): Status => {
    switch (status) {
      case 'COMPLETED_WITH_SUCCESS':
        return {
          statusText: 'Success',
          statusColor: StatusChipBg.GREEN,
        }
      case 'SUBMISSION_FAILED_BY_AWS':
        return { statusText: 'Failed', statusColor: StatusChipBg.RED }
      case 'SUBMISSION_IN_PROGRESS':
        return { statusText: 'Pending', statusColor: StatusChipBg.YELLOW }
      default:
        return { statusText: status, statusColor: StatusChipBg.GREEN }
    }
  }

  const productTabs = [
    {
      key: 'app-settings',
      product: 'oppsync',
      label: (
        <div className={classes.tab}>
          Contracts{' '}
          <div className={classes.label}>
            {visibleContractData.length < 10
              ? `0${visibleContractData.length}`
              : visibleContractData.length.toString()}{' '}
            /{' '}
            {contracts.length < 10
              ? `0${contracts.length}`
              : contracts.length.toString()}
          </div>
        </div>
      ),
      renderElement: (
        <div className={classes.contractsContainer}>
          <ContractsList
            visibleContractData={visibleContractData}
            setVisibleContractData={setVisibleContractData}
            contractPage={contractPage}
            setContractPage={setContractPage}
          />
        </div>
      ),
    },
    {
      key: 'crm',
      product: 'oppsync',
      label: <div className={classes.tab}>Revenue </div>,
      renderElement: (
        <div className={classes.contractsContainer}>
          {revenues && revenues.length > 0 ? (
            revenues.map(revenue => {
              return (
                <RevenueCard
                  key={revenue.labraSubscriptionId}
                  offerName={revenue.privateOfferName}
                  productName={revenue.productTitle}
                  lastInvoiceDate={revenue.invoiceDate}
                  lastDisbursedReceivedOn={revenue.lastDisbursedReceivedDate}
                  lastDisbursedAmount={revenue.lastDisbursedAmount}
                  paymentTerms={revenue.paymentTerms}
                  nextPaymentDueDate={revenue.paymentDueDate}
                  totalDisbursed={revenue.totalReceived}
                  pendingAmount={
                    revenue.tcv ? revenue.tcv - revenue.totalReceived : 0
                  }
                  subscriptionId={revenue.labraSubscriptionId}
                />
              )
            })
          ) : (
            <div className={classes.emptyStateContainer}>
              <OverviewEmptyState
                imgUrl={OverviewEmptyStateImage}
                containerHeight={50}
                imageHeight={12}
                imageWidth={16.75}
                description="No revenue has been generated by your product(s)."
              />
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'admin',
      product: 'oppsync',
      label: (
        <div className={classes.tab}>
          Contacts{' '}
          <div className={classes.label}>
            {contacts.length < 10
              ? `0${contacts.length}`
              : contacts.length.toString()}
          </div>
        </div>
      ),
      renderElement: (
        <div className={classes.contractsContainer}>
          {contacts && contacts.length > 0 ? (
            contacts.map(contact => {
              return (
                <ContactCard
                  key={contact.email}
                  email={contact.email}
                  firstName={contact.firstName}
                  lastName={contact.lastName}
                  createdAt={contact.createdAt}
                  associatedOffers={contact.associatedOffers}
                />
              )
            })
          ) : (
            <div className={classes.emptyStateContainer}>
              <OverviewEmptyState
                imgUrl={OverviewEmptyStateImage}
                containerHeight={50}
                imageHeight={12}
                imageWidth={16.75}
                description="No buyer contact is associated with your product(s)."
              />
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'user',
      product: 'oppsync',
      label: <div className={classes.tab}>Usage metering </div>,
      renderElement: (
        <div className={classes.contractsContainer}>
          {meterings && meterings.length > 0 ? (
            meterings.map(metering => {
              return (
                <MeteringCard
                  key={metering.subscriptionId}
                  offerName={metering.offerName || ''}
                  productName={metering.productName}
                  productId={metering.productId}
                  subscriptionId={metering.subscriptionId}
                  growthPercentage={metering.growthRatePercent}
                  lastMeteringDate={metering.lastMeteringDate}
                  lastMeteringStatus={mapMeteringStatus(
                    metering.lastMeteringStatus
                  )}
                  lastMeteringAmount={metering.lastMeteringAmount}
                  meteringSubmissions={metering.meteringCount}
                  dimensionWiseMetering={metering.dimensionWiseMetering}
                  subscriptionStatus={metering.subscriptionStatus}
                  subscriptionStatusChip={mapContractStatus(
                    metering.subscriptionStatus
                  )}
                />
              )
            })
          ) : (
            <div className={classes.emptyStateContainer}>
              <OverviewEmptyState
                imgUrl={OverviewEmptyStateImage}
                containerHeight={50}
                imageHeight={12}
                imageWidth={16.75}
                description="No metering has been sent for your product(s)."
              />
            </div>
          )}
        </div>
      ),
    },
  ]
  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const handleProductTypeTabChange = (index: number) => {
    setActiveTabIndex(index)
  }

  const selectedContent = productTabs?.find(
    (_, index) => index === activeTabIndex
  )?.renderElement

  return (
    <div data-testid="buyer-details-tab-section">
      <div className={classes.tabs}>
        <MainTabsV2
          tabs={productTabs}
          onChange={handleProductTypeTabChange}
          activeTabIndex={activeTabIndex}
        />
      </div>
      <div className={classes.detailsContainer}>{selectedContent}</div>
    </div>
  )
}

export const mapContractStatus = (status: string): Status => {
  switch (status) {
    case 'SUBSCRIPTION_SUCCESS':
      return {
        statusText: 'Contract started',
        statusColor: StatusChipBg.GREEN,
      }
    case 'UNSUBSCRIBE_SUCCESS':
      return {
        statusText: 'Contract unsubscribed',
        statusColor: StatusChipBg.RED,
      }
    case 'OFFER_EMAIL_SENT':
      return { statusText: 'Offer sent', statusColor: StatusChipBg.LIGHTBLUE }
    case 'OFFER_VIEWED':
      return {
        statusText: 'Offer viewed',
        statusColor: StatusChipBg.LIGHTBLUE,
      }
    case 'OFFER_ACCEPTED':
      return { statusText: 'Offer accepted', statusColor: StatusChipBg.GREEN }
    case 'OFFER_EXPIRED':
      return { statusText: 'Offer expired', statusColor: StatusChipBg.YELLOW }
    case 'OFFER_RESTRICTED':
      return { statusText: 'Offer cancelled', statusColor: StatusChipBg.RED }
    default:
      return { statusText: status, statusColor: StatusChipBg.GREEN }
  }
}

export const formatDateString = (
  dateString: string | undefined | null,
  timeZone: string
): string => {
  if (!dateString) {
    return '-'
  }

  const dateTime = DateTime.fromISO(dateString, { zone: timeZone })
  return dateTime.toFormat(standardLabraDateFormat)
}
